import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, type PanInfo } from 'framer-motion';
import { addDays, format, isSameDay, isSameMonth } from 'date-fns';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { CalendarEventDto } from '@cc/types';

interface Props {
  monthStart: Date;
  gridStart: Date;
  events: CalendarEventDto[];
  accountColors: Record<string, string>;
  onEventClick: (e: CalendarEventDto) => void;
  onDayClick: (d: Date) => void;
}

const VISIBLE_EVENTS_PER_DAY = 3;

/**
 * Simple month grid: 6 weeks × 7 days. Each cell shows up to 3 event chips
 * (account-colour-coded) and a "+ N more" overflow link if it overflows.
 * Click a day → jump to week view for that day. Click an event chip → open the
 * detail sheet for that event. Drag a chip onto another day to reschedule
 * (date changes; time of day preserved).
 */
export function MonthView({
  monthStart,
  gridStart,
  events,
  accountColors,
  onEventClick,
  onDayClick,
}: Props) {
  const queryClient = useQueryClient();
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)), [gridStart]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEventDto[]>();
    for (const e of events) {
      const key = format(new Date(e.startTime), 'yyyy-MM-dd');
      const list = map.get(key) ?? [];
      list.push(e);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
    return map;
  }, [events]);

  const moveMutation = useMutation({
    mutationFn: (args: { id: string; startIso: string; endIso: string }) =>
      api.updateCalendarEvent(args.id, {
        startIso: args.startIso,
        endIso: args.endIso,
        notifyAttendees: false,
      }),
    onSuccess: () => {
      toast.success('Moved.');
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: () => toast.error('Could not move event.'),
  });

  function commitDrop(event: CalendarEventDto, fromKey: string, toKey: string) {
    if (fromKey === toKey) return;
    const fromDate = parseYmd(fromKey);
    const toDate = parseYmd(toKey);
    const dayDiff = Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
    if (dayDiff === 0) return;
    const newStart = new Date(new Date(event.startTime).getTime() + dayDiff * 24 * 60 * 60_000);
    const newEnd = new Date(new Date(event.endTime).getTime() + dayDiff * 24 * 60 * 60_000);
    moveMutation.mutate({
      id: event.id,
      startIso: newStart.toISOString(),
      endIso: newEnd.toISOString(),
    });
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <div className="grid grid-cols-7 border-b border-border bg-muted/30">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6">
        {days.map((d) => {
          const key = format(d, 'yyyy-MM-dd');
          const list = byDay.get(key) ?? [];
          const inMonth = isSameMonth(d, monthStart);
          const isToday = isSameDay(d, new Date());
          const visible = list.slice(0, VISIBLE_EVENTS_PER_DAY);
          const overflow = Math.max(0, list.length - VISIBLE_EVENTS_PER_DAY);
          return (
            <div
              key={key}
              data-month-day-key={key}
              onClick={() => onDayClick(d)}
              className={cn(
                'relative min-h-[96px] cursor-pointer border-b border-r border-border p-1 text-left transition-colors hover:bg-accent/20',
                !inMonth && 'bg-muted/10 text-muted-foreground',
                isToday && 'bg-accent/20',
              )}
            >
              <div className="mb-1 flex items-center justify-between text-[11px]">
                <span className={cn(isToday && 'font-semibold')}>{format(d, 'd')}</span>
                {list.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">{list.length}</span>
                )}
              </div>
              <div className="space-y-0.5">
                {visible.map((e) => (
                  <MonthEventChip
                    key={e.id}
                    event={e}
                    color={accountColors[e.accountId] ?? '#888'}
                    sourceKey={key}
                    onClick={() => onEventClick(e)}
                    onDrop={(toKey) => commitDrop(e, key, toKey)}
                  />
                ))}
                {overflow > 0 && (
                  <p className="px-1 text-[10px] text-muted-foreground">+{overflow} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Single event chip with framer drag. On release, looks up the day cell under
 * the pointer and reports its data-month-day-key back to the parent so the
 * date delta can be computed. */
function MonthEventChip({
  event,
  color,
  sourceKey,
  onClick,
  onDrop,
}: {
  event: CalendarEventDto;
  color: string;
  sourceKey: string;
  onClick: () => void;
  onDrop: (toKey: string) => void;
}) {
  const [dragging, setDragging] = useState(false);

  function handleDragEnd(_e: unknown, info: PanInfo) {
    setDragging(false);
    const point = info.point;
    if (!point) return;
    const els = document.elementsFromPoint(point.x, point.y);
    const cell = els.find((el) => (el as HTMLElement).dataset?.monthDayKey) as HTMLElement | undefined;
    const toKey = cell?.dataset?.monthDayKey;
    if (!toKey || toKey === sourceKey) return;
    onDrop(toKey);
  }

  return (
    <motion.button
      drag
      dragMomentum={false}
      dragElastic={0}
      dragSnapToOrigin
      onDragStart={() => setDragging(true)}
      onDragEnd={handleDragEnd}
      whileDrag={{ scale: 1.05, zIndex: 30, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
      onClick={(ev) => {
        ev.stopPropagation();
        if (dragging) return;
        onClick();
      }}
      className={cn(
        'block w-full cursor-grab truncate rounded px-1 py-0.5 text-left text-[10px]',
        dragging && 'cursor-grabbing',
      )}
      style={{
        backgroundColor: `${color}33`,
        borderLeft: `2px solid ${color}`,
      }}
    >
      {event.isAllDay
        ? event.summary || '(no title)'
        : `${format(new Date(event.startTime), 'h:mm')} ${event.summary || '(no title)'}`}
    </motion.button>
  );
}

function parseYmd(k: string): Date {
  const parts = k.split('-');
  const [yStr, mStr, dStr] = parts;
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  return new Date(y, m - 1, d);
}
